import MatchEndModal, { type RatingMove } from "../play/match-end/MatchEndModal";
import { demoMatchProgress } from "../../shared/progress";
import type { Unlock } from "../play/match-end/UnlockCelebration";
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import createMatch from "core/src/core/match.js";
import * as random from "core/src/core/util/random.js";

import type { Match } from "../../shared/types";
import { createMap, COLOR_IDS, BOT_NAMES } from "../lobby/lobby";
import { VICTORY_ANIMS } from "../../game-canvas/victory-anim";
import GameOverlay from "../../game-canvas/overlays/overlay-controller.js";

import GameComponent from "../play/game/LocalGame";
import GameOverComponent from "../play/game-over/GameOver";
import MatchOverComponent from "../play/match-over/MatchOver";
import RankInsignia from "../../shared/rating/RankInsignia";
import { TIERS } from "../../shared/rating/tiers";

// ---------------------------------------------------------------------------
// DEBUG-ONLY routes (/debug/game, /debug/round-over, /debug/match-over).
//
// These mount the real in-game / round-over / match-over screens with a mock
// match, so we can iterate the layout and styling at any width WITHOUT playing
// a match to completion. Not linked anywhere in the UI - just type the URL.
// Query params: ?players=N (default 12), ?map=Square 500 | Circle 800 | ...
// ---------------------------------------------------------------------------

function mockMatchConfig(players: number, mapString: string, maxScore = 30, humans = 2) {
  const list: any[] = [];
  for (let i = 0; i < players; i++) {
    const human = i < humans;
    list.push({
      type: human ? "human" : "bot",
      colorId: COLOR_IDS[i % COLOR_IDS.length],
      name: human && i === 0 ? "You" : human && i === 1 ? "Player 2" : BOT_NAMES[i % BOT_NAMES.length] || "Bot " + i,
      left: i === 0 ? "A" : i === 1 ? "LEFT" : null,
      right: i === 0 ? "S" : i === 1 ? "RIGHT" : null,
      // Every mock bot celebrates (cycling the six), so /debug/round-over's
      // victory lap always demos an animation whoever wins.
      victoryAnim: human ? null : VICTORY_ANIMS[i % VICTORY_ANIMS.length],
      flag: human ? null : ["se", "de", "us", "jp", "br", "fr"][i % 6],
      id: "d" + i,
    });
  }
  return { players: list, map: createMap(mapString), maxScore };
}

// Step a fresh round to completion so a finished round has real trails, a
// winner and varied scores (synchronous; a guard caps it so it always returns).
function playOutRound(match: Match) {
  const game: any = match.prepareNextGame(random.generateSeed());
  game.start();
  // Bigger steps + a tight guard keep the synchronous play-out fast even with a
  // big roster (we just need a finished-looking round for design, not realtime).
  let guard = 0;
  while (game.isActive() && guard++ < 1500) game.update(0.05);
  match.addFinishedGameState(game.gameState);
}

function buildMockMatch(finishedRounds: number, players: number, mapString: string, maxScore = 30, humans = 2): Match {
  const match = createMatch({ matchConfig: mockMatchConfig(players, mapString, maxScore, humans) }) as Match;
  for (let r = 0; r < finishedRounds; r++) playOutRound(match);
  return match;
}

// A map big enough that N worms can spawn without colliding (mirrors the lobby's
// "bigger map for bigger games"). Overridable via ?map=.
function defaultMapFor(players: number): string {
  if (players > 20) return "Square 900";
  if (players > 10) return "Square 720";
  if (players > 4) return "Square 560";
  return "Square 460";
}

function useDebugParams() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const players = Math.max(2, Math.min(40, Number(params.get("players")) || 12));
  const map = params.get("map") || defaultMapFor(players);
  // ?rounds=N forces the finished-round count (e.g. ?rounds=0 to see match-over
  // with no Rounds section, like an online match). Undefined = view's default.
  const roundsRaw = params.get("rounds");
  const rounds = roundsRaw === null ? undefined : Math.max(0, Math.min(20, Number(roundsRaw) || 0));
  // ?max=N: the first-to target (a ranked duel: ?players=2&max=3)
  const max = Math.max(1, Number(params.get("max")) || 30);
  // ?humans=0..2: how many of the seats are keyboard players (default 2). 0 lets
  // the whole round play itself, for recording gameplay clips.
  const humansRaw = params.get("humans");
  const humans = humansRaw === null ? 2 : Math.max(0, Math.min(2, Number(humansRaw) || 0));
  return { players, map, rounds, max, humans };
}

const noop = () => {};

export function DebugGame() {
  const { players, map, rounds, max, humans } = useDebugParams();
  const [match] = useState(() => buildMockMatch(rounds ?? 0, players, map, max, humans));
  const [overlay] = useState(() => GameOverlay());
  return (
    <div className="page-main page-main-game">
      <GameComponent match={match} overlay={overlay} onGameOverAction={noop} onLeaveAction={noop} isLocal />
    </div>
  );
}

export function DebugRoundOver() {
  const { players, map } = useDebugParams();
  // Keep the played-out round's live game and hand it over as the lapGame, so
  // the screen runs the real victory lap (winner keeps driving + their victory
  // animation plays) exactly like real local play.
  const [built] = useState(() => {
    const match = createMatch({ matchConfig: mockMatchConfig(players, map) }) as Match;
    const game: any = match.prepareNextGame(random.generateSeed());
    game.start();
    // Real-sized ticks (unlike playOutRound's coarse 50ms steps): with big steps
    // the last worms regularly die in the SAME step, leaving no lap winner - and
    // this screen exists precisely to watch the winner's victory lap.
    let guard = 0;
    while (game.isActive() && guard++ < 8000) game.update(0.016);
    match.addFinishedGameState(game.gameState);
    return { match, lapGame: game };
  });
  const [overlay] = useState(() => GameOverlay());
  return (
    <div className="page-main page-main-game">
      <GameOverComponent
        match={built.match}
        overlay={overlay}
        lapGame={built.lapGame}
        onStartNextGameAction={noop}
        onMatchOverAction={noop}
        onLeaveAction={noop}
        isLocal
      />
    </div>
  );
}

export function DebugMatchOver() {
  const { players, map, rounds } = useDebugParams();
  const [match] = useState(() => buildMockMatch(rounds ?? 3, players, map));
  return (
    <div className="page-main page-main-game">
      <MatchOverComponent
        match={match}
        onRoundClick={noop}
        onExitAction={noop}
        onPlayAgain={noop}
        // a settled rating for the first seat, so the "Your rating" card shows
        playerRatings={{ [match.matchConfig.players[0].id]: { value: 1148, delta: 85 } }}
        mainPlayerId={match.matchConfig.players[0].id}
      />
    </div>
  );
}

// Visual showcase of the rank insignia (every ladder step), so the look can be
// reviewed without real ratings.
export function DebugRanks() {  const row: React.CSSProperties = { display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", margin: "10px 0" };
  const h: React.CSSProperties = { color: "#8995b3", marginTop: 24, fontFamily: "Orbitron, sans-serif", fontSize: 13, letterSpacing: 1 };
  // Every step of the ladder: three ratings inside each sub-tiered tier's span;
  // Master and Wormlord are single tiers (no sub-tiers), so one badge each.
  const ladder: { label: string; value: number }[] = [];
  TIERS.forEach((t, i) => {
    if (i >= TIERS.length - 2) {
      ladder.push({ label: t.name, value: t.min + 50 });
      return;
    }
    const span = TIERS[i + 1].min - t.min;
    for (let s = 0; s < 3; s++) ladder.push({ label: "", value: Math.round(t.min + (span * (s + 0.5)) / 3) });
  });
  return (
    <div className="page-main" style={{ padding: 24, color: "#eaf0ff" }}>
      <h1 style={{ fontFamily: "Orbitron, sans-serif" }}>Rank badges</h1>

      <h3 style={h}>Insignia ladder (Ladder K) — every step, Bronze I → Wormlord</h3>
      <div style={row}>
        {ladder.map((step) => (
          <RankInsignia key={step.value} value={step.value} size={44} text="stacked" />
        ))}
      </div>

      <h3 style={h}>Insignia with text beside / number</h3>
      <div style={row}>
        <RankInsignia value={1049} size={22} text="beside" />
        <RankInsignia value={1049} size={32} text="beside" showNumber />
        <RankInsignia value={1420} size={22} text="beside" />
      </div>

      <h3 style={h}>Sizes (leaderboard 22 · account 30 · lobby 36)</h3>
      <div style={row}>
        <RankInsignia value={1000} size={22} text="beside" />
        <RankInsignia value={1000} size={30} text="beside" />
        <RankInsignia value={1000} size={36} />
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// /debug/match-end: the match-end modal (stage 1 of the end-of-match flow) in
// each of its rating scenarios, on a button bar, so the count-up, the
// promotion blast, the demotion crumble and the first-match hatch can be
// watched without playing a rated match per case. Each press remounts the
// modal so its animation starts from the top.
// ---------------------------------------------------------------------------
const MATCH_END_PLAYERS = [
  { id: "p1", name: "You", colorId: "blue", flag: "se", type: "human" },
  { id: "p2", name: "HardLeft", colorId: "green", flag: "dk", type: "human" },
  { id: "p3", name: "Quickdraw", colorId: "pink", flag: "au", type: "human" },
  { id: "p4", name: "SolarFlare", colorId: "orange", flag: "tr", type: "human" },
];
const MATCH_END_SCORE = { p2: 12, p1: 9, p3: 7, p4: 4 };
type MatchEndScenario = { label: string; move: RatingMove | null; arrived: boolean; guest?: boolean; won?: boolean; signedIn?: boolean; currentRank?: { rating: number | null; games: number }; modeLabel?: string; unlocks?: Unlock[]; unlocksLate?: boolean };
// Tier bounds (shared/rating/tiers.ts): silver 700, gold 1000, platinum 1300.
const MATCH_END_SCENARIOS: MatchEndScenario[] = [
  { label: "Rank up (silver -> gold)", move: { before: 975, after: 1040 }, arrived: true },
  { label: "Rank down (gold -> silver)", move: { before: 1012, after: 958 }, arrived: true },
  // "same rank" stays inside one sub-band (Gold I is 1000..1100); a step to the
  // next sub-band wraps the bar to that band's start, so it gets its own button.
  { label: "Rating up, same rank", move: { before: 1020, after: 1072 }, arrived: true },
  { label: "Rating down, same rank", move: { before: 1072, after: 1024 }, arrived: true },
  { label: "Sub-tier up (Gold I -> II)", move: { before: 1080, after: 1130 }, arrived: true },
  // Below Bronze's 400 is still Bronze I: a climb to 457 is no rank change.
  { label: "Up from under 400 (Bronze I)", move: { before: 371, after: 457 }, arrived: true },
  { label: "Down under 400 (Bronze I)", move: { before: 457, after: 371 }, arrived: true },
  { label: "Bronze I -> II", move: { before: 371, after: 520 }, arrived: true },
  { label: "First rated match (hatch)", move: { before: 0, after: 1040, firstMatch: true }, arrived: true },
  { label: "Placement 1 of 5", move: { before: 0, after: 0, placement: { played: 1, of: 5 } }, arrived: true },
  { label: "Placement 4 of 5", move: { before: 0, after: 0, placement: { played: 4, of: 5 } }, arrived: true },
  { label: "You won (rank up)", move: { before: 975, after: 1040 }, arrived: true, won: true },
  { label: "Guest (unranked)", move: null, arrived: true, guest: true },
  { label: "Waiting for ratings", move: null, arrived: false, signedIn: true },
  { label: "Ranked, waiting (Gold I)", move: null, arrived: false, signedIn: true, currentRank: { rating: 1040, games: 12 }, modeLabel: "Ranked 1 vs 1" },
  { label: "Ranked, waiting (placing)", move: null, arrived: false, signedIn: true, currentRank: { rating: null, games: 2 }, modeLabel: "Ranked 1 vs 1" },
  // Achievements unlocked this match (they play once the rating card lands).
  { label: "Unlock: first match", move: { before: 0, after: 1040, firstMatch: true }, arrived: true, unlocks: [{ id: "certified-worm", level: 1, eggs: 2 }] },
  { label: "Unlock: win + level up", move: { before: 1020, after: 1072 }, arrived: true, won: true, unlocks: [{ id: "food-chain", level: 2, eggs: 3 }, { id: "podium-regular", level: 1, eggs: 2 }] },
  { label: "Unlock: big haul", move: { before: 1275, after: 1310 }, arrived: true, won: true, unlocks: [{ id: "climber", level: 1, eggs: 5 }, { id: "giant-slayer", level: 1, eggs: 4 }, { id: "rocket-worm", level: 1, eggs: 7 }, { id: "the-grind", level: 3, eggs: 5 }] },
  { label: "Unlock: level V", move: { before: 1640, after: 1652 }, arrived: true, unlocks: [{ id: "power-hungry", level: 5, eggs: 20 }] },
  { label: "Unlock: ranked duel", move: { before: 1180, after: 1204 }, arrived: true, won: true, modeLabel: "Ranked 1 vs 1", unlocks: [{ id: "duelist", level: 1, eggs: 2 }, { id: "ranked-regular", level: 2, eggs: 5 }] },
  { label: "Unlock: arrives late", move: { before: 1020, after: 1004 }, arrived: true, unlocks: [{ id: "gravity", level: 1, eggs: 3 }], unlocksLate: true },
];

export function DebugMatchEnd() {
  React.useEffect(() => { demoMatchProgress(); }, []);
  const [scenario, setScenario] = useState(0);
  const [run, setRun] = useState(1); // bumps the key so the modal remounts and replays
  const sc = MATCH_END_SCENARIOS[scenario];
  // "arrives late": the achievement_update lands well after the rating card.
  const [lateIn, setLateIn] = useState(false);
  React.useEffect(() => {
    setLateIn(false);
    const t = window.setTimeout(() => setLateIn(true), 4500);
    return () => window.clearTimeout(t);
  }, [run, scenario]);
  return (
    <div style={{ minHeight: "100vh", background: "#070a12" }}>
      <div style={{ position: "fixed", left: 12, top: 12, zIndex: 2000, display: "flex", flexWrap: "wrap", gap: 6, maxWidth: 360 }}>
        {MATCH_END_SCENARIOS.map((s, i) => (
          <button
            key={s.label}
            className={"app-btn app-btn-sm " + (i === scenario ? "app-btn-primary" : "app-btn-ghost")}
            onClick={() => { setScenario(i); setRun((r) => r + 1); }}
          >
            {s.label}
          </button>
        ))}
        <button className="app-btn app-btn-sm app-btn-ghost" onClick={() => setRun((r) => r + 1)}>Replay</button>
      </div>
      <MatchEndModal
        key={run}
        players={MATCH_END_PLAYERS}
        score={MATCH_END_SCORE}
        myPlayerId={sc.won ? "p2" : "p1"}
        myMove={sc.move}
        ratingsArrived={sc.arrived}
        signedIn={sc.guest ? false : sc.signedIn}
        currentRank={sc.currentRank}
        modeLabel={sc.modeLabel}
        myUnlocks={sc.unlocksLate && !lateIn ? null : sc.unlocks ?? null}
        onSeeResults={() => setRun((r) => r + 1)}
      />
    </div>
  );
}
